import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { FolderDetail } from '@persped/shared';
import { useState } from 'react';
import { useReprintFolderLabel } from '../../api/queries';
import { ProblemAlert } from '../../components/ProblemAlert';

/**
 * Sérült címke újranyomtatása (B9; a D fázis 5.3 pontja után a webé).
 *
 * MIÉRT CSAK REGISZTRÁLT MAPPÁRA: a regisztráció ELŐTT megsérült címkét
 * eldobjuk, a sorszám elvész, és friss címkével kezdünk — ehhez nem kell
 * rendszerfunkció. Az újranyomtatás csak ott értelmes, ahol a mappa teljes
 * története már ahhoz a vonalkódhoz kötődik.
 *
 * MIÉRT KELL FOTÓ: a megerősítés arról szól, hogy a felhasználó AZONOSÍTOTTA
 * a mappát. Gerincfotó nélkül nincs mihez hasonlítani, ezért a szerver is
 * elutasítja — itt csak előre jelezzük.
 *
 * A `rowVersion` visszaküldése fordítja a UI-megerősítést szerver által
 * kikényszerített invariánssá: ha a gerincszöveg közben változott, a
 * felhasználó elavult információ alapján erősített meg.
 */
export function ReprintLabelCard({ folder }: { folder: FolderDetail }) {
  const reprint = useReprintFolderLabel();
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const kep = folder.images[0] ?? null;
  const kesz = Boolean(kep) && reason.trim().length > 0;

  return (
    <Paper variant="outlined">
      <Typography variant="subtitle1" sx={{ p: 2, pb: 1 }}>
        Címke újranyomtatása
      </Typography>
      <Divider />
      <Stack spacing={2} sx={{ p: 2 }}>
        {!kep && (
          <Alert severity="warning">
            Ehhez a mappához nincs gerincfotó, ezért a címke nem nyomtatható újra — nem lenne mihez
            hasonlítani a megerősítéskor.
          </Alert>
        )}

        {kep && !confirmed && (
          <>
            <Alert severity="info">
              Az újranyomtatás UGYANAZT a vonalkódot adja ki még egyszer. Ellenőrizd, hogy a lenti
              fotó és gerincszöveg tényleg ahhoz a mappához tartozik, amelyiknek a címkéje
              megsérült — egy másik mappára ragasztott azonos vonalkód két mappát tenne
              megkülönböztethetetlenné.
            </Alert>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {folder.barcode}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {folder.currentText ?? '(nincs gerincszöveg)'}
            </Typography>
            {/*
              `alignSelf`, mert a szülő `Stack` alapból nyújtja a gyerekeit —
              enélkül a gomb teljes szélességű lesz. A TELJES SZÉLESSÉG a
              TC22-é (kicsi, kézi eszköz); a weben egy sorvégig érő gomb
              tévesen elsődleges műveletnek látszik (2026-08-14-i CR #6).
            */}
            <Button
              variant="outlined"
              onClick={() => setConfirmed(true)}
              sx={{ alignSelf: 'flex-start' }}
            >
              Ez a mappa — folytatom
            </Button>
          </>
        )}

        {kep && confirmed && (
          <>
            <TextField
              label="Indoklás"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Pl. a címke leszakadt a szállítás során"
              required
              fullWidth
            />
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setConfirmed(false)}>Vissza</Button>
              <Button
                variant="contained"
                disabled={!kesz || reprint.isPending}
                onClick={() =>
                  reprint.mutate(
                    {
                      barcode: folder.barcode,
                      confirmedImageId: kep!.id,
                      rowVersion: folder.rowVersion,
                      reason: reason.trim(),
                    },
                    {
                      onSuccess: () => {
                        setReason('');
                        setConfirmed(false);
                      },
                    },
                  )
                }
              >
                Újranyomtatás
              </Button>
            </Stack>
          </>
        )}

        {reprint.isError && <ProblemAlert error={reprint.error} />}
        {reprint.isSuccess && (
          <Alert severity={reprint.data.printStatus === 'SENT' ? 'success' : 'warning'}>
            {/*
              A `printStatus` ADAT, nem kivétel: a FAILED/UNCONFIRMED is 201-gyel
              érkezik. A `hint` kész magyar mondat a következő lépésről — szó
              szerint jelenítjük meg, nem írjuk újra.
            */}
            {reprint.data.hint ?? 'A címke nyomtatásra ment.'}
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}
